/*
 * eventStats — the single set of definitions for event numbers
 * (IA audit 2026-08-14: three pages disagreed on "Photos", two on the
 * sessions dedupe, two on share rate). Every v2 surface computes
 * through these helpers; changing a definition here changes it
 * everywhere at once.
 */

import moment from 'moment-timezone';

/* Activation dates are CALENDAR dates, but the API serializes them as
   UTC midnight ("2026-08-20T00:00:00Z"); a local-zone parse lands a
   day early anywhere west of UTC — an event named 20260820 renders as
   8/19 and misses every is-it-today check. Anchor to the UTC calendar
   day and return a LOCAL-midnight moment. The only way v2 parses an
   activation (or job) date. */
export function eventDay(
    date: string | Date | null | undefined,
): moment.Moment {
    if (date == null) return moment.invalid();
    return moment(moment.utc(date).format('YYYY-MM-DD'), 'YYYY-MM-DD');
}

/* Sessions dedupe: the API's LEFT JOINs return one row per share —
   key by photoSessionId, falling back to the row id so sessions
   without a photoSessionId still count (Insights used to drop them). */
export function dedupeSessions<T extends Record<string, any>>(
    rows: T[] | null | undefined,
): T[] {
    const seen = new Set<unknown>();
    return (rows ?? []).filter((s) => {
        const key = s.photoSessionId ?? s.id;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
}

/* Shares dedupe: by share id. */
export function dedupeShares<T extends Record<string, any>>(
    rows: T[] | null | undefined,
): T[] {
    const seen = new Set<unknown>();
    return (rows ?? []).filter((s) => {
        if (s.id == null || seen.has(s.id)) return false;
        seen.add(s.id);
        return true;
    });
}

/* CANONICAL Photos (Josh, 2026-08-14: "the number of images uploaded
   to the cloud") — non-hidden gallery media: processed photos and
   processed videos. Excludes derivatives (prints, composites,
   originals kept for reprocessing). Matches Media's "Photos" chip. */
export function photosUploaded(
    mediaRows: Array<Record<string, any>> | null | undefined,
): number {
    return (mediaRows ?? []).filter((m) => {
        if (m.isHidden) return false;
        const st = m.smileportType ?? '';
        return (
            st === 'processed' ||
            st === 'processed-video' ||
            (st === '' && (m.mimeType ?? '').startsWith('image/'))
        );
    }).length;
}

/* Estimate from session totals — used only where fetching media rows
   is too heavy (Home's busy grid). Labeled an estimate on purpose. */
export function sessionsPhotoEstimate(
    sessions: Array<Record<string, any>>,
): number {
    return sessions.reduce((sum, s) => sum + (Number(s.total) || 0), 0);
}

/* Share rate (Sharing's definition, decided): sessions with at least
   one share ÷ total sessions. Pass DEDUPED sessions. */
export function shareRate(
    sessions: Array<Record<string, any>>,
    shares: Array<Record<string, any>>,
): number | null {
    if (sessions.length === 0) return null;
    const shared = new Set(
        shares.map((s) => s.photoSessionId).filter(Boolean),
    );
    return Math.min(
        100,
        Math.round((shared.size / sessions.length) * 100),
    );
}
